/**
 * Street furniture under a vehicle: what gives, what bends, what is
 * flattened, and what stands.
 *
 * A building, a tree, a rock or a fountain is fixed: a vehicle crushes into
 * it as into a wall or a post. Most else in a street is not.
 *
 * - **Things on fixings** (`PROP_BEHAVIOUR`): a bin bolted to its post, a
 *   postbox, a bench, a street lamp, a fence's panels. Each weighs what it
 *   does and is so stiff against a car's crumple zone. Its fixings hold
 *   against a hit closing slower than `holds`: below that it stands, bent
 *   or knocked crooked by how near it came to giving (`bendFor`), and the
 *   vehicle stops as at a post. Above, it comes free and the hit is shared
 *   by weight, as between two vehicles (`core/vehicle/collision.ts`): a
 *   25 kg bin is flung off ahead, a little faster than the car, and the car
 *   hardly slows; the crushing is shared by stiffness, so a soft, light bin leaves
 *   no mark on the car. A street lamp stands on a breakaway base, as
 *   Swedish roads' passively safe posts do: it shears off at about 13 km/h
 *   and costs the car a few km/h and a dented bumper, not its front.
 * - **Plants** (`SOFT`): a hedge or a bush is driven through. It slows the
 *   vehicle while it pushes through, and is flattened.
 */

/** How a prop on fixings takes a hit. */
export interface PropBehaviour {
  /** Its weight, kg. */
  mass: number;
  /** How stiff it is against a car's crumple zone (1). */
  stiffness: number;
  /** The closing speed its fixings hold against, m/s; faster and it comes free. */
  holds: number;
  /** How far over it is bent by a hit just short of breaking it free, radians. */
  bends: number;
}

export const PROP_BEHAVIOUR: Readonly<Record<string, PropBehaviour>> = {
  // A litter bin bolted to its post: thin sheet steel, about 25 kg with what is in it.
  bin: { mass: 25, stiffness: 0.25, holds: 2, bends: 0.5 },
  postbox: { mass: 45, stiffness: 0.5, holds: 3, bends: 0.35 },
  // A park bench, wood on cast iron feet, bolted down.
  bench: { mass: 70, stiffness: 0.45, holds: 2.5, bends: 0.25 },
  // A street lamp on a breakaway base: shears at about 13 km/h.
  lamp: { mass: 150, stiffness: 1, holds: 3.6, bends: 0.3 },
  // A fence's 4 m panel: pickets on two rails, or post and rail.
  picket: { mass: 35, stiffness: 0.2, holds: 1.5, bends: 0.6 },
  rail: { mass: 55, stiffness: 0.3, holds: 2.2, bends: 0.5 },
  // A bus shelter: an aluminium frame and three panes of toughened glass,
  // light and soft. Anything more than a creep breaks the glass and the car
  // goes on through; see `SHELTER_FRAME`.
  shelter: { mass: 250, stiffness: 0.3, holds: 0.8, bends: 0.15 },
};

/**
 * Struck harder than this, m/s of its own change in speed, a bus shelter's
 * frame comes apart and is thrown; softer, it stands, bent, its glass gone.
 */
export const SHELTER_FRAME = 4;

/** Plants a vehicle drives through: how hard they drag at it, m/s², and how far out they reach at scale 1, metres. */
export const SOFT: Readonly<Record<string, { drag: number; radius: number }>> = {
  // Ploughing through a dense hedge takes some 20 kJ: 4-5 km/h off a car at 45.
  hedge: { drag: 9, radius: 0.35 },
  shrub: { drag: 3, radius: 0.6 },
  dryShrub: { drag: 2, radius: 0.5 },
};

/**
 * How a prop of `species` at `scale` takes a hit, or undefined if it is
 * fixed. A fence panel stretched along its length weighs the more for it.
 */
export function propBehaviour(species: string, scale = 1): PropBehaviour | undefined {
  const b = PROP_BEHAVIOUR[species];
  if (b === undefined) return undefined;
  return species === 'picket' || species === 'rail' ? { ...b, mass: b.mass * scale } : b;
}

/**
 * How far over a prop is bent by a hit closing at `closing` m/s that its
 * fixings held: nothing for a nudge, up to `bends` just short of giving.
 */
export function bendFor(b: PropBehaviour, closing: number): number {
  const t = Math.max(0, Math.min(1, (closing - 0.35 * b.holds) / (0.65 * b.holds)));
  return b.bends * t;
}

/**
 * How a prop knocked free is thrown, from its change in velocity `dv` m/s
 * along unit `push` (scene x, z), and its half-height: velocity (x, y, z)
 * and spin (rad/s about x, y, z). Struck low, it goes off the way it was
 * pushed and up a little, its foot kicked out from under it so its top
 * falls back toward what hit it, as a post's does. `roll` is a dice in [0, 1)
 * for a little spread.
 */
export function thrown(push: { x: number; z: number }, dv: number, halfHeight: number, roll: number):
  { velocity: [number, number, number]; spin: [number, number, number] } {
  const lift = 0.4 + dv * 0.12;
  const wobble = (roll - 0.5) * 0.4;
  // The foot going the push's way faster than the top: a turn about push × up.
  const turn = (dv / Math.max(0.3, halfHeight)) * 0.6;
  return {
    velocity: [push.x * dv - push.z * dv * wobble, lift, push.z * dv + push.x * dv * wobble],
    spin: [push.z * turn, (roll - 0.5) * 3, -push.x * turn],
  };
}
